import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppSettings } from 'src/app/config';
import { MaterialModule } from 'src/app/material.module';
import { MenuService } from 'src/app/services/apps/menu/menu.service';
import { SessionPermissionService } from 'src/app/services/apps/auth/session-permission.service';
import {
  SidebarCountersService,
  SidebarCountersState,
} from 'src/app/services/apps/service-order/sidebar-counters.service';
import { CoreService } from 'src/app/services/core.service';
import { Module } from 'src/assets/types/interface/module.interface';
import { Program } from 'src/assets/types/interface/program.interface';
import { HeaderComponent } from './vertical/header/header.component';
import { IconMenuComponent } from './vertical/sidebar/icon-menu/iconmenu.component';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

@Component({
  selector: 'app-full',
  standalone: true,
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    IconMenuComponent,
  ],
  templateUrl: './full.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit {
  menuItems: Module[] = [];
  pageContentMode: 'inherit' | 'centered' | 'expanded' = 'inherit';

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;
  selectedIcon = 1;
  isInicialSeted = false;

  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private sidebarCountersSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private htmlElement!: HTMLHtmlElement;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  onIconSelected(icon: number) {
    if (!this.isInicialSeted) {
      this.isInicialSeted = true;
      return;
    }

    if (icon === this.selectedIcon) {
      this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    } else {
      this.options.sidenavCollapsed = false;
    }
    this.selectedIcon = icon;
    this.options.sidenavOpened = true;
    this.settings.setOptions(this.options);
  }
  constructor(
    private settings: CoreService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private menuService: MenuService,
    private sessionPermissionService: SessionPermissionService,
    private sidebarCountersService: SidebarCountersService,
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.content.scrollTo({ top: 0 });
        this.updatePageContentMode();
        this.syncSidebarCountersActivation();
        this.applySidebarCounters(this.sidebarCountersService.counters$.value);
      });
  }

  ngOnInit(): void {
    this.updatePageContentMode();
    this.syncSidebarCountersActivation();
    this.sidebarCountersSubscription =
      this.sidebarCountersService.counters$.subscribe((counters) => {
        this.applySidebarCounters(counters);
      });

    this.menuService.getMenu().subscribe({
      next: (data) => {
        this.menuItems = data.sort((a, b) => a.menu_position - b.menu_position);

        this.menuItems.forEach((item) => {
          if (item.programs) {
            item.programs.sort((a, b) => a.menu_position - b.menu_position);
          }
        });
        this.applySidebarCounters(this.sidebarCountersService.counters$.value);
      },
      error: (err: HttpErrorResponse) => {
        localStorage.setItem('access_token', '');
        localStorage.setItem('general_access_token', '');
        localStorage.setItem('user', '');
        localStorage.setItem('tenant', '');
        this.router.navigate(['/authentication/login']);
        console.error('Erro ao carregar o menu:', err);
      },
    });
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.sidebarCountersSubscription.unsubscribe();
    this.sidebarCountersService.destroy();
  }

  toggleCollapsed() {
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  isPageCentered(): boolean {
    if (this.pageContentMode === 'centered') {
      return true;
    }

    if (this.pageContentMode === 'expanded') {
      return false;
    }

    return this.options.boxed;
  }

  private updatePageContentMode() {
    let route = this.router.routerState.root;

    while (route.firstChild) {
      route = route.firstChild;
    }

    this.pageContentMode =
      (route.snapshot.data['pageContentMode'] as
        | 'inherit'
        | 'centered'
        | 'expanded'
        | undefined) ?? 'inherit';
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.htmlElement.classList.add('light-theme');
  }

  private syncSidebarCountersActivation(): void {
    this.sidebarCountersService.setActive(true);
  }

  private applySidebarCounters(counters: SidebarCountersState): void {
    if (this.sessionPermissionService.getTypeTenant() !== 'Tomador') {
      return;
    }

    const serviceOrderProgram = this.findProgramByRoute(
      '/gestao/ordens-servico',
    );
    const approvalProgram = this.findProgramByRoute('/gestao/aprovacoes');
    this.applyProgramChip(
      serviceOrderProgram,
      counters.serviceOrdersActionPendingTotal,
      'bg-accent text-white',
    );
    this.applyProgramChip(
      approvalProgram,
      counters.approvalsTotal,
      'bg-accent text-white',
    );
  }

  private findProgramByRoute(route: string): Program | undefined {
    return this.menuItems
      .flatMap((item) => item.programs ?? [])
      .find((program) => program.route === route);
  }

  private formatPendingCount(count: number): string {
    return count > 99 ? '99+' : String(count);
  }

  private applyProgramChip(
    program: Program | undefined,
    total: number,
    chipClass: string,
  ) {
    if (!program) {
      return;
    }

    if (total <= 0) {
      program.chip = false;
      program.chipContent = undefined;
      program.chipClass = undefined;
      return;
    }

    program.chip = true;
    program.chipContent = this.formatPendingCount(total);
    program.chipClass = chipClass;
  }
}
